<template>
  <div class="ag-grid-detail-renderer">
    <div
      v-if="showDetail === detailMapping['detail'] && !callDetails"
      class="ag-custom-loading-cell p-l-15 p-t-5"
      style="line-height: 25px;"
    >
      <i class="fa fa-spinner fa-pulse fa-fw" /> 
      <span v-t="'LOADING_CALL_DETAILS'" />
    </div>
     
    <BlockNumberForm
      v-if="showDetail === detailMapping['blockNumber']"
      :show-loader="showCallLogActionLoaderStatus"
      :campaigns="campaigns"
      :phone-number="inboundNumber"
      :cached-number="cachedData"
      :campaign-id="params.node.data.campaignId"
      @closeForm="closeForm"
      @setDetailRowHeight="setDetailRowHeight"
      @blockNumberSubmit="blockNumberSubmit"
      @cacheFormData="cacheFormData"
    />

    <CallAnnotateForm
      v-if="showDetail === detailMapping['annotate']"
      :show-loader="showCallLogActionLoaderStatus"
      :recent-tags="recentTags"
      :cached-data="cachedData"
      @closeForm="closeForm"
      @setDetailRowHeight="setDetailRowHeight"
      @annotateCallSubmit="annotateCallSubmit"
      @cacheFormData="cacheFormData"
    />

    <CallPaymentOverrideForm
      v-if="showDetail === detailMapping['adjustPayment']"
      :cached-data="cachedData"
      :is-admin="isAdmin"
      :show-loader="showCallLogActionLoaderStatus"
      :call="params.node.data"
      @setDetailRowHeight="setDetailRowHeight"
      @closeForm="closeForm"
      @callPaymentAdjustmentSubmit="callPaymentAdjustmentSubmit"
      @cacheFormData="cacheFormData"
    />

    <tabs
      v-if="showDetail === detailMapping['detail'] && callDetails"
      v-model="currentTabIndex"
      :transition-duration="0"
      custom-nav-class="nav-compact"
      class="p-t-3"
      @change="handleTabChange"
    >
      <tab
        :title="$t('EVENTS')"
        class="p-b-5"
      >
        <EventsCallLogDetail
          :panel-tab-index="0"
          :current-tab-index="currentTabIndex"
          :events="mainEvents"
          :time-zone-id="timeZoneId"
          :role="role"
          @setDetailRowHeight="setDetailRowHeight"
          @addFilter="addFilter"
        />
      </tab>
      <tab
        :title="$t('RECORD')"
        class="p-b-5"
      >
        <CallRecordsCallLogDetail
          :panel-tab-index="1"
          :current-tab-index="currentTabIndex"
          :call-record="callRecord"
          :col-defs="params.context.componentParent.columnDefs"
          :column-types="params.context.componentParent.columnTypes"
          @setDetailRowHeight="setDetailRowHeight"
          @addFilter="addFilter"
        />
      </tab>
      <tab
        v-if="displayTags"
        title="Tags"
        class="p-b-5"
      >
        <TagsCallLogDetail
          :panel-tab-index="2"
          :current-tab-index="currentTabIndex"
          :grouped-tags="groupedTags"
          :tag-actions-available="tagActionsAvailable"
          @setDetailRowHeight="setDetailRowHeight"
          @addFilter="addFilter"
          @addTagAsColumn="addTagAsColumn"
        />
      </tab>
      <tab
        v-if="hasRingTreeEvents"
        :title="$t('RING_TREE_EVENTS')"
        class="p-b-5"
      >
        <EventsCallLogDetail
          :panel-tab-index="3"
          :current-tab-index="currentTabIndex"
          :events="ringTreeEvents"
          :time-zone-id="timeZoneId"
          :role="role"
          @setDetailRowHeight="setDetailRowHeight"
          @addFilter="addFilter"
        />
      </tab>
      <tab
        v-if="hasTranscription || hasRecording"
        :title="$t('TRANSCRIPTION')"
        class="p-b-5"
      >
        <TranscriptionCallLogDetail
          :panel-tab-index="transcriptionTabIndex"
          :current-tab-index="currentTabIndex"
          :time-zone-id="timeZoneId"
          :role="role"
          :call-details="callDetails"
          :has-transcription="hasTranscription"
          :is-transcription-pending="isTranscriptionPending"
          @setDetailRowHeight="setDetailRowHeight"
        />
      </tab>
    </tabs>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Vue from 'vue';
import TranscriptionCallLogDetail from './TranscriptionCallLogDetail';
import EventsCallLogDetail from './EventsCallLogDetail';
import TagsCallLogDetail from './TagsCallLogDetail';
import CallRecordsCallLogDetail from './CallRecordsCallLogDetail';
import BlockNumberForm from '../BlockNumberForm';
import CallAnnotateForm from '../CallAnnotateForm';
import CallPaymentOverrideForm from '../CallPaymentOverrideForm';
import { detailMapping } from '../callLogActionFormsMixin';
import { filter } from 'lodash';
import messages from '../locales';

export default Vue.extend({
  name: 'CallLogDetailCellRenderer',
  components: {
    TranscriptionCallLogDetail,
    EventsCallLogDetail,
    TagsCallLogDetail,
    CallRecordsCallLogDetail,
    BlockNumberForm,
    CallAnnotateForm,
    CallPaymentOverrideForm,
  },
  data() {
    return {
      callId: '',
      currentTabIndex: 0,
      showDetail: 0,
      campaigns: [],
      inboundNumber: undefined,
      detailMapping: {...detailMapping},
      cachedData: {},
      ringTreeEventsMapping: Object.freeze({
        'PingRAWResult': true,
        'PingSuccess': true,
        'PingFailure': true,
        'SecondPingRAWResult': true,
        'SecondPingSuccess': true,
        'SecondPingFailure': true,
      }),
    };
  },

  computed: {
    groupedTags() {
      if(!this.callDetails['message-tags']) {
        return {};
      }
      return this.callDetails['message-tags'].reduce((finalValue, tag) => {
        finalValue[tag.type] = finalValue[tag.type] || [];

        finalValue[tag.type].push(tag);

        return finalValue;
      }, {});
    },

    showCallLogActionLoaderStatus() {
      return this.params.context.componentParent.showCallLogActionLoaderStatus[this.callId];
    },

    closeFormStatus() {
      return this.params.context.componentParent.callLogActionFormVisibilityStatus[this.callId];
    },

    displayTags() {
      return this.params.context.componentParent.role === 'admin'
      || this.params.context.componentParent.role === 'user'
      || this.params.context.componentParent.role === 'publisher'
      || (this.params.context.componentParent.role === 'buyer' && Object.keys(this.groupedTags).length > 0);
    },

    isAdmin() {
      return this.params.context.componentParent.isAdmin && !this.params.context.componentParent.isBuyer;
    },

    role() {
      return this.params.context.componentParent.role;
    },

    recentTags() {
      return this.params.context.componentParent.recentCallTags;
    },

    callDetails() {
      return this.params.context.componentParent.callLogDetailsMapping[this.callId];
    },

    timeZoneId() {
      return this.params.context.componentParent.timeZoneId;
    },

    hasRingTreeEvents() {
      for (let i = 0; i < this.callDetails.events.length; i++) {
        if (this.ringTreeEventsMapping[this.callDetails.events[i].name]) {
          return true;
        }
      }

      return false;
    },

    callRecord() {
      const callRecord = {...this.callDetails};

      delete callRecord['message-tags'];
      delete callRecord.events;
      delete callRecord.transcriptionDetails;

      return callRecord || {};
    },

    mainEvents() {
      return filter(this.callDetails.events, (e) => {
        return !this.ringTreeEventsMapping[e.name];
      });      
    },

    ringTreeEvents() {
      return filter(this.callDetails.events, (e) => {
        return this.ringTreeEventsMapping[e.name];
      });      
    },

    tagActionsAvailable() {
      return this.role !== 'buyer';
    },

    transcriptionTabIndex() {
      if (!this.displayTags && !this.hasRingTreeEvents) return 2;
      if ((this.displayTags && !this.hasRingTreeEvents) || (!this.displayTags && this.hasRingTreeEvents)) return 3;
      return 4;
    },

    hasTranscription() {
      return this.callDetails?.hasTranscription;
    },

    isTranscriptionPending() {
      return this.callDetails?.pendingTranscription;
    },

    hasRecording() {
      return this.callDetails?.hasRecording;
    },
  },

  watch: {
    closeFormStatus(newValue) {
      if (newValue === false) {
        this.closeForm();
      }
    },
    callDetails: {
      immediate: false,
      async handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          if (this.currentTabIndex === this.transcriptionTabIndex) {
            await this.checkAndFetchTranscription();
          }
          this.handleDynamicTabOpen();
        }
      }
    },
  },

  beforeMount () {
    this.callId = this.params.data.inboundCallId;
    this.showDetail = this.params.data.showForm || detailMapping['detail'];
    this.currentTabIndex = this.params.context.componentParent.tabIndexCacheForDetailOpenCalls[this.callId] || 0;
    this.campaigns = this.params.context.componentParent.campaigns;
    this.inboundNumber = this.params.data.inboundPhoneNumber;
    this.cachedData = this.params.context.componentParent.expandedDetailRowNodes[this.callId]?.data;
  },

  methods: {
    ...mapActions('reporting', [
      'getCallTranscription',
    ]),
    setDetailRowHeight(height) {
      this.params.node.setRowHeight(height);
      this.params.eGridCell.style.height = `${height}px`;
      this.params.node.gridApi.onRowHeightChanged();
      this.params.node.setExpanded(false);
      this.params.node.setExpanded(true);
    },
    closeForm() {
      this.params.context.componentParent.closeForm(this.params.node.parent);
    },
    blockNumberSubmit(number) {
      this.params.context.componentParent.blockNumber(number, this.callId);
    },
    annotateCallSubmit(tag) {
      this.params.context.componentParent.annotateCall(tag, this.callId);
    },
    callPaymentAdjustmentSubmit(data) {
      this.params.context.componentParent.callPaymentDispute(data, this.callId);
    },
    handleDynamicTabOpen() {
      // Handle cases where user needs to open call details and go directly to a different tab than the first
      if (this.callDetails) {
        const tab = this.params.context.componentParent.expandedDetailRowNodes[this.callId]?.tab;
        if (tab && tab === 'transcription') {
          this.currentTabIndex = this.transcriptionTabIndex;
          this.handleTabChange(this.currentTabIndex);
        }
      }
    },
    async handleTabChange(tabIndex) {
      this.params.context.componentParent.setTabIndexCacheForCall(this.callId, tabIndex);
      if (tabIndex === this.transcriptionTabIndex) {
        await this.checkAndFetchTranscription();
      }
    },
    cacheFormData(data) {
      this.params.context.componentParent.setDataCacheForActionForm(this.callId, data);
    },
    addFilter({ colId, value, filterGroupName, type, isTag, originalTag }) {
      this.params.context.componentParent.addFilter({ colId, value, filterGroupName, type, isTag, originalTag });
    },
    addTagAsColumn(tag) {
      this.params.context.componentParent.addTagAsColumn(tag);
    },
    async checkAndFetchTranscription() {
      if (this.hasTranscription && !this.callDetails?.transcriptionDetails) {
        await this.getCallTranscription(this.callDetails);
      }
    },
  },

  i18n: {
    locale: 'en',
    messages
  }
});
</script>

<style lang="scss" scoped>
.ag-grid-detail-renderer {
  form {
    box-shadow: inset 0 -6px 10px -9px var(--component-ag-grid__detail-shadow-color);
  }
}
</style>